import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import dayjs from 'dayjs';
import { datadogLogs } from '@datadog/browser-logs';
import { getOnOffConfigOrElse } from '../config/config.service';
import _ from 'lodash';

/* tslint:disable */
/**
 * Les niveaux de log disponibles pour le logger
 */
export enum Level {
  /**
   * Log désactivé
   */
  OFF = 0,
  /**
   * Afficher seulement les erreurs
   */
  ERROR = 1,
  /**
   * Afficher seulement les warning
   */
  WARN = 2,
  /**
   * Afficher le niveau de log info
   */
  INFO = 3,
  /**
   * Afficher le niveau de log debug
   */
  DEBUG = 4,
  /**
   * Afficher tous les logs
   */
  LOG = 5,
}

/**
 * For browsers that don't implement the debug method, log will be used instead. Fixes #62.
 */
const CONSOLE_DEBUG_METHOD: string = typeof console.debug === 'function' ? 'debug' : 'log';

/**
 * LoggerWrapper service
 *
 * Appelle la console et respecte les niveaux de log
 */
@Injectable({ providedIn: 'root' })
export class LoggerWrapper {
  /**
   * Le nom de la classe à afficher dans le logger
   *
   * @type {string}
   */
  protected name: string = '';

  protected enableDatadogLogs: boolean = false;

  /**
   * Le niveau de log à utiliser pour ce logger
   */
  protected level: Level;

  constructor() {
    this.level = environment.logLevel;
  }

  /**
   * Set le nom de la classe (provider, component)
   * Pour l'afficher dans les logs
   * @param object La classe où on veut afficher le logger
   */
  setName(object: any): void {
    this.name = object.constructor.name;
  }

  setEnableDatadogLogs(enableDatadogLogs: boolean): void {
    this.enableDatadogLogs = enableDatadogLogs;
  }

  isDatadogLogsToggleEnabled(): boolean {
    return getOnOffConfigOrElse('TOGGLE_DATADOG_LOGS', 'OFF') === 'ON';
  }

  /**
   * Affiche le message dans la console d'erreur
   * @param error the JS error
   * @param message le message à afficher dans la console
   */
  error(error: Error, ...message: any[]): void {
    try {
      if (this.level >= Level.ERROR) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        console.error.apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.error(messageForDD, tags, error);
        }
      }
    } catch (error) {
      this.errorLog(error, "Une erreur est survenue lors du log d'error");
    }
  }

  /**
   * Affiche le message dans la console de warning
   * @param message le message à afficher dans la console
   */
  warn(...message: any[]): void {
    try {
      if (this.level >= Level.WARN) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        console.warn.apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.warn(messageForDD, tags);
        }
      }
    } catch (error) {
      this.errorLog(error, 'Une erreur est survenue lors du log de warn');
    }
  }

  /**
   * Affiche le message dans la console d'info
   * @param message le message à afficher dans la console
   */
  info(...message: any[]): void {
    try {
      if (this.level >= Level.INFO) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        console.info.apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.info(messageForDD, tags);
        }
      }
    } catch (error) {
      this.errorLog(error, "Une erreur est survenue lors du log d'info");
    }
  }

  /**
   * Affiche le message dans la console de debug
   * @param message le message à afficher dans la console
   */
  debug(...message: any[]): void {
    try {
      if (this.level >= Level.DEBUG) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        (<any>console)[CONSOLE_DEBUG_METHOD].apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.debug(messageForDD, tags);
        }
      }
    } catch (error) {
      this.errorLog(error, 'Une erreur est survenue lors du log de debug');
    }
  }

  /**
   * Affiche le message dans la console de log
   * @param message le message à afficher dans la console
   */
  log(...message: any[]): void {
    try {
      if (this.level >= Level.LOG) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        console.log.apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.log(messageForDD, tags);
        }
      }
    } catch (error) {
      this.errorLog(error, 'Une erreur est survenue lors du log de log');
    }
  }

  private errorLog(error: Error, ...message: any[]) {
    try {
      if (this.level >= Level.ERROR) {
        let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
        console.error.apply(console, msgArray);
        if (this.isDatadogLogsToggleEnabled() && this.enableDatadogLogs) {
          const messageForDD = this.formatLogForDatadog(message);
          const tags = this.formatTagsForDatadog(messageForDD);
          datadogLogs.logger.error(messageForDD, tags, error);
        }
      }
    } catch (error) {}
  }

  private formatLogForDatadog(...message: any[]): string {
    let msgArray = [`#[${this.name} ${this._getDate()}]`].concat(message);
    // recursivité ? _.deepCopy ?
    return msgArray.map((m) => JSON.stringify(_.cloneDeep(m))).join(' ');
  }

  private formatTagsForDatadog(messageForDD: string) {
    return {
      EVENT_MESSAGE: messageForDD,
    };
  }

  /**
   * @private
   * @returns la date courante sous forme de chaine
   */
  private _getDate(): string | null {
    return dayjs().format('YYYY-MM-DD HH:mm:ss');
  }
}

/* tslint:enable */
